import { useState } from 'react'
import { Button } from '@agms/ui'
import { useQueryClient } from '@tanstack/react-query'
import { applicationApi, type Application } from '../../lib/api'
import { usePermissions } from '../../hooks/useAuth'
import { apiErrorMessage, apiValidationErrors } from '../../hooks/useErrorHandler'

interface ValidiFIStatusCardProps {
  application: Application
}

/**
 * Human-friendly labels for the BankValidationResult flag constants.
 * Every entry has:
 *   - `title`: short, plain-English, what the user sees first
 *   - `detail`: one-line explanation of what it means practically
 *   - `nextStep`: a concrete action the operator can take
 *
 * Keep in sync with `App\Services\Adapters\Contracts\Dto\BankValidationResult`
 * `FLAG_*` constants on the API side.
 */
const FLAG_LABELS: Record<
  string,
  { title: string; detail: string; nextStep: string }
> = {
  avc_below_threshold: {
    title: "Bank couldn't confirm the account is valid",
    detail:
      "ValidiFI's verification score was below the safe threshold. This usually means the account number is mistyped, the account is too new, or the bank returned uncertain data.",
    nextStep:
      'Re-verify the routing and account numbers with the merchant digit-by-digit, correct Step 4 if needed, then re-check.',
  },
  account_not_open: {
    title: 'Account appears to be closed or inactive',
    detail:
      "The bank's records show this account as closed or invalid. The merchant may have given us an old account number.",
    nextStep:
      'Ask the merchant to confirm the account is currently open and active. Request a recent voided check to verify.',
  },
  not_ach_capable: {
    title: 'Account cannot accept ACH transfers',
    detail:
      "This routing and account combination isn't set up to receive ACH deposits, which is how Fiserv funds the merchant.",
    nextStep:
      'Ask the merchant for a different account that accepts ACH (usually a business checking account).',
  },
  ownership_mismatch: {
    title: "Owner's name doesn't match the account",
    detail:
      "The name on file at the bank doesn't match the beneficial owner in Step 6. This could be a typo, a joint account, or the wrong person.",
    nextStep:
      "Confirm the owner's legal name matches what's on their bank records. Check Step 6 for typos in the name fields.",
  },
  nsf_history: {
    title: 'Recent insufficient-funds activity',
    detail:
      'The account has a recent history of returned ACH transactions (non-sufficient funds).',
    nextStep:
      'Review with management before continuing — NSF history is a risk signal that may require additional underwriting.',
  },
  validation_unavailable: {
    title: 'Bank verification is temporarily unavailable',
    detail:
      'ValidiFI did not complete the verification. This is usually a vendor credential, service, or connectivity issue, not proof that the merchant bank account is invalid.',
    nextStep:
      'Re-check after the ValidiFI connection is fixed. If the same message returns, contact engineering before asking the merchant for new banking details.',
  },
  missing_banking_info: {
    title: 'Banking details are missing',
    detail:
      'The eSign was completed without the routing number or account number, so ValidiFI could not run the bank verification.',
    nextStep:
      'Collect the bank name, routing number, and account number from the merchant, save Step 4, then re-check the bank.',
  },
}

/**
 * Explainer content for the AVC score info tooltip.
 *
 * AVC = "Account Verification Confidence", ValidiFI's scoring scale.
 * 0 = couldn't verify anything · 11 = fully verified with clean signals.
 * AGMS's threshold is 6 (configurable via VALIDIFI_AVC_THRESHOLD).
 */
const AVC_TOOLTIP = `AVC (Account Verification Confidence) is ValidiFI's 0–11 scoring scale for how confident they are that a bank account is valid and low-risk. 0 = couldn't verify, 11 = fully verified and clean. AGMS requires a score of 6 or higher to auto-advance to Fiserv submission.`

function scoreLabel(score: number | null): string {
  if (score === null) return 'Unknown'
  if (score <= 2) return 'Very high risk'
  if (score <= 5) return 'High risk'
  if (score <= 8) return 'Moderate confidence'
  return 'High confidence'
}

/**
 * Derive a flag list from application columns when the stored
 * `validifi_raw_response.flags` is missing or empty. Defensive
 * fallback so users never see a red card with no reasons.
 */
function deriveFlags(app: Application, threshold = 6): string[] {
  const out: string[] = []
  if ((app.validifi_avc_score ?? 0) < threshold) out.push('avc_below_threshold')
  if (app.validifi_account_status && app.validifi_account_status !== 'open')
    out.push('account_not_open')
  if (app.validifi_ownership_match === false) out.push('ownership_mismatch')
  if (app.validifi_nsf_flag === true) out.push('nsf_history')
  return out
}

/**
 * Small info icon with a native tooltip. Uses `title` for zero-dep
 * accessibility — no portal, no positioning logic, works on touch.
 * The `tabIndex=0` ensures keyboard users can focus it too.
 */
function InfoIcon({ tooltip }: { tooltip: string }) {
  return (
    <span
      tabIndex={0}
      title={tooltip}
      aria-label={tooltip}
      className="inline-flex items-center justify-center w-4 h-4 ml-1 rounded-full
                 bg-slate-200 text-slate-600 text-[10px] font-semibold cursor-help
                 hover:bg-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-400"
    >
      i
    </span>
  )
}

/**
 * ValidiFI Step 4 / pipeline status card.
 *
 * Renders nothing until ValidiFI has been run (`validifi_checked_at`
 * is non-null). After that, one of three states:
 *
 *   - **Clean**: green card, short confirmation.
 *   - **Flagged**: red card with plain-English reasons, ordered
 *     remediation steps, a "Re-check bank" button, and a collapsed
 *     Manager Override section at the bottom.
 *   - **Override applied**: green card noting an override is in
 *     effect with a link to the audit log.
 *
 * Design goals (from user feedback on Session 16):
 *   - Lead with plain English, not jargon. "Bank account couldn't
 *     be verified" instead of "ValidiFI flagged AVC0".
 *   - Always show WHY it failed. Never render an empty reasons list.
 *   - Always show WHAT TO DO NEXT before offering override.
 *   - Override is last-resort, collapsed behind a toggle.
 *
 * @see CLAUDE.md Section 11 — ValidiFI Bank Validation Integration
 */
export function ValidiFIStatusCard({ application }: ValidiFIStatusCardProps) {
  const queryClient = useQueryClient()
  const { canRecheckValidiFI, canOverrideValidiFI } = usePermissions()
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [rechecking, setRechecking] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [recheckMessage, setRecheckMessage] = useState<string | null>(null)
  const [showOverride, setShowOverride] = useState(false)

  // Pre-validation: don't render anything until ValidiFI has been run
  if (!application.validifi_checked_at) {
    return null
  }

  const score = application.validifi_avc_score
  const passed = application.validifi_passed === true
  const storedFlags = application.validifi_raw_response?.flags ?? []
  // Fallback: if the stored flags list is empty but the result didn't
  // pass, derive flags from the individual column values. This
  // guarantees the reasons section is never empty on a flagged card.
  const flags =
    storedFlags.length > 0
      ? storedFlags
      : passed
      ? []
      : deriveFlags(application)
  const hasFlags = flags.length > 0
  const validationUnavailable = flags.includes('validation_unavailable')
  const isClean = passed && !hasFlags
  const isOverridden = passed && hasFlags

  const handleRecheck = async () => {
    setRecheckMessage(null)
    setError(null)
    setRechecking(true)
    try {
      const result = await applicationApi.recheckValidiFI(application.id)
      setRecheckMessage(result.message)
      // Give the job a couple seconds to complete then refetch
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['application', application.lead_id] })
        queryClient.invalidateQueries({ queryKey: ['lead', application.lead_id] })
      }, 3000)
    } catch (err) {
      setError(apiErrorMessage(err, 'Re-check failed. Please try again or contact engineering.'))
    } finally {
      setRechecking(false)
    }
  }

  const handleOverride = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (note.trim().length < 10) {
      setError('Override note must be at least 10 characters')
      return
    }

    setSubmitting(true)
    try {
      const result = await applicationApi.overrideValidiFI(application.id, note.trim())
      setSuccessMessage(result.message)
      setNote('')
      await queryClient.invalidateQueries({ queryKey: ['application', application.lead_id] })
    } catch (err) {
      const apiErrors = apiValidationErrors(err)?.note?.[0]
      setError(apiErrors ?? 'Override failed; check the server logs and try again.')
    } finally {
      setSubmitting(false)
    }
  }

  // ----- Clean validation: green card -----
  if (isClean) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex gap-3">
          <svg className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <div>
            <h4 className="text-sm font-medium text-green-900">
              Bank account verified
            </h4>
            <p className="mt-1 text-sm text-green-700">
              ValidiFI confirmed the account is open, matches the owner on file, and is ACH-capable.
            </p>
            <p className="mt-2 text-xs text-green-700/80">
              Confidence score: <span className="font-mono font-semibold">{score} of 11</span> ({scoreLabel(score)})
              <InfoIcon tooltip={AVC_TOOLTIP} />
            </p>
          </div>
        </div>
      </div>
    )
  }

  // ----- Override applied: green card with audit note -----
  if (isOverridden) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex gap-3">
          <svg className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h4 className="text-sm font-medium text-green-900">
              Manager override in effect
            </h4>
            <p className="mt-1 text-sm text-green-700">
              The bank verification originally flagged {flags.length} issue
              {flags.length === 1 ? '' : 's'}, but an authorized manager has cleared the hold.
              The application can advance to Fiserv submission. The override is recorded in the audit log.
            </p>
            <p className="mt-2 text-xs text-green-700/80">
              Original confidence score: <span className="font-mono font-semibold">{score} of 11</span>
              <InfoIcon tooltip={AVC_TOOLTIP} />
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (validationUnavailable) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 space-y-5">
        <div className="flex gap-3">
          <svg className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <div className="flex-1">
            <h4 className="text-base font-semibold text-amber-900">
              Bank verification is unavailable
            </h4>
            <p className="mt-1 text-sm text-amber-800">
              ValidiFI did not complete the verification. This application is paused
              until the verification can run successfully or a manager clears the hold.
            </p>
            <p className="mt-2 text-xs text-amber-700">
              This is a vendor/auth/connectivity result, not a confirmed bad bank account.
            </p>
          </div>
        </div>

        <div>
          <h5 className="text-sm font-semibold text-amber-900 mb-2">What to do next</h5>
          <ol className="list-decimal list-inside space-y-1.5 text-sm text-amber-800 ml-1">
            <li>Click <span className="font-medium">Re-check bank</span> after the ValidiFI connection is fixed.</li>
            <li>If it still returns unavailable, contact engineering before asking the merchant for new banking details.</li>
            <li>Use manager override only when banking has been verified by another approved method.</li>
          </ol>
        </div>

        {canRecheckValidiFI() && (
          <div className="border-t border-amber-200 pt-4 flex items-center justify-between gap-3">
            <div className="text-xs text-amber-700/90">
              {recheckMessage ? (
                <span className="text-green-700">{recheckMessage}</span>
              ) : (
                <>Re-run verification after the ValidiFI connection is fixed.</>
              )}
            </div>
            <Button
              type="button"
              onClick={handleRecheck}
              disabled={rechecking}
              variant="outline"
            >
              {rechecking ? 'Re-checking…' : 'Re-check bank'}
            </Button>
          </div>
        )}

        {canOverrideValidiFI() && (
          <div className="border-t border-amber-200 pt-4">
            {!showOverride ? (
              <button
                type="button"
                onClick={() => setShowOverride(true)}
                className="text-xs text-amber-700 hover:text-amber-900 font-medium underline underline-offset-2"
              >
                Manager override (only if remediation has been attempted)
              </button>
            ) : (
              <div>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <h5 className="text-sm font-semibold text-amber-900">Manager override</h5>
                    <p className="mt-1 text-xs text-amber-700">
                      Use only when the account has been confirmed valid by another approved method.
                      This action is audit-logged with your user account and the reason you provide.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowOverride(false)
                      setNote('')
                      setError(null)
                    }}
                    className="text-xs text-amber-700 hover:text-amber-900"
                  >
                    Cancel
                  </button>
                </div>
                <form onSubmit={handleOverride} className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-amber-900 mb-1">
                      Why is it safe to clear this hold? (minimum 10 characters)
                    </label>
                    <textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      rows={3}
                      placeholder="e.g. Verified routing and account on voided check uploaded today. Merchant confirmed account is active."
                      className="w-full rounded-md border border-amber-300 px-3 py-2 text-sm
                                 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                      disabled={submitting}
                    />
                  </div>
                  {error && <p className="text-xs text-amber-800">{error}</p>}
                  {successMessage && <p className="text-xs text-green-700">{successMessage}</p>}
                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      disabled={submitting || note.trim().length < 10}
                      variant="outline"
                    >
                      {submitting ? 'Submitting…' : 'Override hold'}
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </div>
        )}

        {error && !showOverride && (
          <p className="text-xs text-amber-800">{error}</p>
        )}
      </div>
    )
  }

  // ----- Flagged validation: the redesigned card -----
  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-5 space-y-5">
      {/* Header */}
      <div className="flex gap-3">
        <svg className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <div className="flex-1">
          <h4 className="text-base font-semibold text-red-900">
            Bank account couldn&apos;t be verified
          </h4>
          <p className="mt-1 text-sm text-red-800">
            ValidiFI checked the bank account on file and couldn&apos;t confirm it&apos;s valid.
            This application is paused and can&apos;t be submitted to Fiserv until the
            account is verified or a manager clears the hold.
          </p>
          <p className="mt-2 text-xs text-red-700">
            Confidence score: <span className="font-mono font-semibold">{score ?? 0} of 11</span>
            {' '}<span className="font-medium">({scoreLabel(score)})</span>
            <InfoIcon tooltip={AVC_TOOLTIP} />
          </p>
        </div>
      </div>

      {/* Why it failed */}
      <div>
        <h5 className="text-sm font-semibold text-red-900 mb-2">What went wrong</h5>
        <ul className="space-y-3">
          {flags.map((flag) => {
            const label = FLAG_LABELS[flag] ?? {
              title: flag,
              detail: 'Unrecognized flag — contact engineering.',
              nextStep: 'Contact engineering to investigate.',
            }
            return (
              <li key={flag} className="text-sm bg-white/60 rounded-md p-3 border border-red-100">
                <p className="font-medium text-red-900">{label.title}</p>
                <p className="mt-1 text-red-800/90">{label.detail}</p>
              </li>
            )
          })}
        </ul>
      </div>

      {/* What to do next */}
      <div>
        <h5 className="text-sm font-semibold text-red-900 mb-2">What to do next</h5>
        <ol className="list-decimal list-inside space-y-1.5 text-sm text-red-800 ml-1">
          <li>Call the merchant and re-verify their routing and account numbers digit-by-digit.</li>
          <li>Ask for a recent voided check and upload it to the Document Center.</li>
          <li>Correct the banking info in <span className="font-medium">Step 4</span> if anything was mistyped.</li>
          <li>Click <span className="font-medium">Re-check bank</span> below to run verification again.</li>
          <li>If the account still can&apos;t be verified, escalate to a manager.</li>
        </ol>
      </div>

      {/* Re-check action */}
      {canRecheckValidiFI() && (
        <div className="border-t border-red-200 pt-4 flex items-center justify-between gap-3">
          <div className="text-xs text-red-700/90">
            {recheckMessage ? (
              <span className="text-green-700">{recheckMessage}</span>
            ) : (
              <>Re-run verification after updating the banking info or voided check.</>
            )}
          </div>
          <Button
            type="button"
            onClick={handleRecheck}
            disabled={rechecking}
            variant="outline"
          >
            {rechecking ? 'Re-checking…' : 'Re-check bank'}
          </Button>
        </div>
      )}

      {/* Manager override — collapsed by default, last-resort */}
      {canOverrideValidiFI() && (
        <div className="border-t border-red-200 pt-4">
          {!showOverride ? (
            <button
              type="button"
              onClick={() => setShowOverride(true)}
              className="text-xs text-red-700 hover:text-red-900 font-medium underline underline-offset-2"
            >
              Manager override (only if remediation has been attempted)
            </button>
          ) : (
            <div>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h5 className="text-sm font-semibold text-red-900">Manager override</h5>
                  <p className="mt-1 text-xs text-red-700">
                    Use only when the account has been confirmed valid by other means
                    (e.g. voided check reviewed, merchant called back). This action is
                    audit-logged with your user account and the reason you provide.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowOverride(false)
                    setNote('')
                    setError(null)
                  }}
                  className="text-xs text-red-600 hover:text-red-800"
                >
                  Cancel
                </button>
              </div>
              <form onSubmit={handleOverride} className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-red-900 mb-1">
                    Why is it safe to clear this hold? (minimum 10 characters)
                  </label>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={3}
                    placeholder="e.g. Verified routing and account on voided check uploaded 4/16. Merchant called and confirmed account is active."
                    className="w-full rounded-md border border-red-300 px-3 py-2 text-sm
                               focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
                    disabled={submitting}
                  />
                </div>
                {error && <p className="text-xs text-red-700">{error}</p>}
                {successMessage && <p className="text-xs text-green-700">{successMessage}</p>}
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    disabled={submitting || note.trim().length < 10}
                    variant="destructive"
                  >
                    {submitting ? 'Submitting…' : 'Override hold'}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}

      {/* Surface any recheck error outside the collapsed override block */}
      {error && !showOverride && (
        <p className="text-xs text-red-700">{error}</p>
      )}
    </div>
  )
}
